import React from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../components/PageLayout';

const Home = () => {
  return (
    <PageLayout title="Home" withPadding={false}>
      <div className="home-page">
        {/* Hero Section */}
        <section className="position-relative py-5 overflow-hidden bg-white">
          <div className="container py-lg-5">
            <div className="row align-items-center g-5">
              <div className="col-lg-6">
                <span className="badge bg-primary-subtle text-primary px-3 py-2 rounded-pill mb-3 fw-bold">
                  <i className="las la-rocket me-2"></i> Free Beta Access Now Available
                </span>
                <h1 className="display-3 fw-bold mb-4" style={{ lineHeight: '1.1' }}>
                  Secure Your Digital <span className="text-primary">Empire</span> with Confidence.
                </h1>
                <p className="lead text-muted mb-4 fs-4">
                  Advanced security scanning, real-time SSL monitoring, and automated vulnerability detection.
                  Get enterprise-grade protection, for free.
                </p>
                <div className="d-flex gap-3 flex-wrap">
                  <Link to="/register" className="btn btn-primary btn-lg shadow-sm">
                    Start Scanning Free <i className="las la-arrow-right ms-2"></i>
                  </Link>
                  <Link to="/demo-check" className="btn btn-outline-dark btn-lg">
                    Run Quick Demo
                  </Link>
                </div>
                <div className="mt-4 pt-2 d-flex align-items-center">
                  <div className="d-flex me-3">
                    {[1, 2, 3, 4].map(i => (
                      <img
                        key={i}
                        src={`/assets/images/user-avatar-${i}.png`}
                        alt="Secure360 Community Member"
                        className="border border-2 border-white rounded-circle ms-n2"
                        style={{ marginLeft: i > 1 ? '-12px' : '0' }}
                      />
                    ))}
                  </div>
                  <p className="mb-0 small text-muted">
                    Joined by <span className="fw-bold text-dark">500+</span> companies this month.
                  </p>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="position-relative">
                  <div className="bg-primary rounded-5 position-absolute top-50 start-50 translate-middle w-100 h-100 opacity-10 blur-3xl"></div>
                  <img
                    src="/assets/images/security-dashboard.jpg"
                    alt="Security Dashboard Mockup"
                    className="img-fluid rounded-4 shadow-lg position-relative border border-light"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* App Promo Strip */}
        <div className="container-fluid bg-primary py-3 overflow-hidden position-relative">
          <Link to="/app" className="text-decoration-none d-block">
            <div className="d-flex align-items-center justify-content-center text-white position-relative z-1">
              <span className="badge bg-white text-primary me-3 fw-bold">NEW</span>
              <span className="fw-bold me-2 d-none d-md-inline">Security on the go:</span>
              <span className="opacity-90">Our first Mobile App is arriving in 2026!</span>
              <i className="las la-arrow-right ms-3 animate-x"></i>
            </div>

            {/* Background decorative icons */}
            <div className="position-absolute top-50 start-0 translate-middle-y ms-5 opacity-25 d-none d-lg-block">
              <i className="las la-mobile fs-2 text-white"></i>
            </div>
            <div className="position-absolute top-50 end-0 translate-middle-y me-5 opacity-25 d-none d-lg-block">
              <i className="las la-shield-alt fs-2 text-white"></i>
            </div>
          </Link>
          <style>{`
          .animate-x { animation: slideX 1.5s infinite; }
          @keyframes slideX {
            0%, 100% { transform: translateX(0); }
            50% { transform: translateX(5px); }
          }
        `}</style>
        </div>

        {/* WordPress Plugin Promo */}
        <section className="py-4 bg-white border-top">
          <div className="container">
            <div className="row align-items-center g-4">
              <div className="col-lg-7">
                <span className="badge bg-primary-subtle text-primary px-3 py-2 rounded-pill mb-3 fw-bold">
                  <i className="las la-plug me-2"></i> New WordPress Plugin
                </span>
                <h2 className="fw-bold mb-2">“Secure360” for WordPress</h2>
                <p className="text-muted mb-0">
                  Install the Secure360 WordPress plugin to keep your site’s security signals and monitoring
                  visible inside your WordPress admin.
                </p>
              </div>
              <div className="col-lg-5 text-lg-end">
                <Link
                  to="/documentation"
                  className="btn btn-primary btn-lg px-4 shadow-sm"
                  style={{ borderRadius: 14 }}
                >
                  View Installation Guide <i className="las la-arrow-right ms-2"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* What We Do / Features Grid */}
        <section className="py-5 bg-light">
          <div className="container py-5">
            <div className="text-center mb-5">
              <h2 className="display-5 fw-bold mb-3">One Platform. Total Protection.</h2>
              <p className="text-muted fs-5 mx-auto max-w-[700px]">
                We've consolidated modern security tools into a single, intuitive dashboard to help you focus on growing your business.
              </p>
            </div>
            <div className="row g-4">
              {[
                {
                  title: 'SSL/TLS Surveillance',
                  icon: 'la-lock',
                  desc: 'Continuous monitoring of your certificate health, expiry dates, and protocol vulnerabilities.',
                  color: 'primary'
                },
                {
                  title: 'Vulnerability Assessment',
                  icon: 'la-shield-alt',
                  desc: 'Deep scans for common exploits, misconfigurations, and outdated software versions.',
                  color: 'success'
                },
                {
                  title: 'Traffic Analysis',
                  icon: 'la-chart-bar',
                  desc: 'Understand your site traffic patterns and detect suspicious activity before it becomes a breach.',
                  color: 'info'
                },
                {
                  title: 'DoS Protection',
                  icon: 'la-wifi',
                  desc: 'Real-world testing and monitoring to ensure your services remain online under pressure.',
                  color: 'warning'
                },
                {
                  title: 'Threat Intelligence',
                  icon: 'la-user-secret',
                  desc: 'Stay updated with global threat feeds integrated directly into your security reports.',
                  color: 'danger'
                },
                {
                  title: 'Instant Alerts',
                  icon: 'la-bell',
                  desc: 'Get notified via Email the moment a security risk is detected on your assets.',
                  color: 'dark'
                }
              ].map((f, i) => (
                <div className="col-lg-4 col-md-6" key={i}>
                  <div className="card border-0 shadow-sm h-100 p-4">
                    <div className={`bg-${f.color} bg-opacity-10 text-${f.color} rounded-3 d-inline-flex align-items-center justify-content-center mb-4`}
                       >
                      <i className={`las ${f.icon} fs-2`}></i>
                    </div>
                    <h3 className="fw-bold mb-3 h4">{f.title}</h3>
                    <p className="text-muted mb-0">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why We Are Unique / Benefits */}
        <section className="py-5 bg-white">
          <div className="container py-5">
            <div className="row align-items-center g-5">
              <div className="col-lg-6 order-2 order-lg-1">
                <img
                  src="/assets/images/security-team.jpg"
                  alt="Security Team"
                  className="img-fluid rounded-4 shadow-sm"
                />
              </div>
              <div className="col-lg-6 order-1 order-lg-2">
                <h2 className="display-5 fw-bold mb-4">Why Businesses Trust Secure360</h2>
                <p className="text-muted mb-4 fs-5">
                  We're not just another scanner. We're a comprehensive security partner that scales with you.
                </p>
                <ul className="list-unstyled mb-5">
                  {[
                    { t: 'Free For Now', d: 'All premium features are completely free during our public beta phase.' },
                    { t: 'Zero Configuration', d: 'Start scanning in under 60 seconds. No complex installations required.' },
                    { t: 'White-Label Reports', d: 'Generate professional PDF reports to share with stakeholders.' },
                    { t: 'Privacy First', d: 'We never store sensitive data. Your security scanning is strictly confidential.' }
                  ].map((item, i) => (
                    <li className="d-flex mb-4" key={i}>
                      <div className="bg-primary-subtle text-primary rounded-circle p-2 me-3 h-[fit-content]">
                        <i className="las la-check"></i>
                      </div>
                      <div>
                        <h3 className="fw-bold mb-1 h5">{item.t}</h3>
                        <p className="text-muted small mb-0">{item.d}</p>
                      </div>
                    </li>
                  ))}

                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Achievements / Stats */}
        <section className="py-5 bg-dark text-white">
          <div className="container py-4">
            <div className="row text-center g-4">
              <div className="col-md-3">
                <h2 className="display-4 fw-bold mb-0">15k+</h2>
                <p className="text-white text-opacity-75 mb-0">Daily Scans</p>
              </div>
              <div className="col-md-3">
                <h2 className="display-4 fw-bold mb-0">1.5M</h2>
                <p className="text-white text-opacity-75 mb-0">Assets Protected</p>
              </div>
              <div className="col-md-3">
                <h2 className="display-4 fw-bold mb-0">50+</h2>
                <p className="text-white text-opacity-75 mb-0">Security Tools</p>
              </div>
              <div className="col-md-3">
                <h2 className="display-4 fw-bold mb-0">24/7</h2>
                <p className="text-white text-opacity-75 mb-0">Real-time Monitoring</p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials / Happy Clients */}
        <section className="py-5 bg-light">
          <div className="container py-5">
            <div className="text-center mb-5">
              <h2 className="fw-bold fs-1">Trusted by Professionals</h2>
              <p className="text-muted">Hear from the security experts who rely on us.</p>
            </div>
            <div className="row g-4">
              {[
                {
                  text: "Secure360 caught an SSL expiry we had missed for months. It saved us a major service outage.",
                  author: "Sarah Jenkins",
                  role: "CTO @ TechFlow",
                  img: "/assets/images/avatar-sarah.jpg"
                },
                {
                  text: "The vulnerability scanner is incredibly thorough. It's now a core part of our deployment pipeline.",
                  author: "Michael Chen",
                  role: "Security Lead @ DevOps Inc",
                  img: "/assets/images/avatar-michael.jpg"
                },
                {
                  text: "Clean interface, fast results, and most importantly, accurate findings. Highly recommended!",
                  author: "Elena Rodriguez",
                  role: "Freelance Pen-Tester",
                  img: "/assets/images/avatar-elena.jpg"
                }
              ].map((t, i) => (
                <div className="col-lg-4" key={i}>
                  <div className="card border-0 shadow-sm p-4 h-100">
                    <div className="mb-3 text-warning">
                      <i className="las la-star"></i><i className="las la-star"></i><i className="las la-star"></i><i className="las la-star"></i><i className="las la-star"></i>
                    </div>
                    <p className="mb-4 text-muted italic">"{t.text}"</p>
                    <div className="d-flex align-items-center mt-auto">
                      <img src={t.img} alt={t.author} className="rounded-circle me-3" width="48" height="48" />
                      <div>
                        <h3 className="fw-bold mb-0 h6">{t.author}</h3>
                        <p className="small text-muted mb-0">{t.role}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Future Plans Section */}
        <section className="py-5 bg-white border-top overflow-hidden">
          <div className="container py-5">
            <div className="position-relative bg-dark rounded-5 p-5 shadow-lg overflow-hidden border border-primary border-opacity-10">
              {/* Background Decorative Icon */}
              <div className="position-absolute top-50 end-0 translate-middle-y me-n4 opacity-10 text-primary text-[280px]" style={{ transform: 'rotate(-15deg)', pointerEvents: 'none' }}>
                <i className="las la-route"></i>
              </div>

              {/* Top Corner Decorative Icon */}
              <div className="position-absolute top-0 start-0 mt-3 ms-3 opacity-25 text-primary text-[40px]">
                <i className="las la-rocket animate-pulse"></i>
              </div>

              <div className="row align-items-end position-relative z-1">
                <div className="col-lg-7">
                  <span className="badge bg-primary px-3 py-2 rounded-pill mb-3 fw-bold text-uppercase tracking-wider small">
                    The Path Ahead
                  </span>
                  <h2 className="display-5 fw-bold mb-3 text-white">Our Strategic Roadmap to 2026</h2>
                  <p className="text-white text-opacity-75 fs-5 mb-4 lh-base max-w-[600px]">
                    We're constantly innovating. In the coming months, we're launching
                    <span className="text-primary fw-bold"> AI-powered threat prediction</span>,
                    one-click remediation, and dedicated mobile security testing.
                  </p>
                  <div className="pt-2">
                    <div className="d-flex align-items-center text-white text-opacity-50 small mb-2">
                      <i className="las la-check-circle text-primary me-2"></i>
                      <span>Completely free for all early adopters during beta</span>
                    </div>
                    <div className="d-flex align-items-center text-white text-opacity-50 small">
                      <i className="las la-check-circle text-primary me-2"></i>
                      <span>Open community contributions ecosystem</span>
                    </div>
                  </div>
                </div>

                <div className="col-lg-5 text-lg-end mt-5 mt-lg-0">
                  {/* Timeline Button */}
                  <div className="mb-4">
                    <Link to="/roadmap" className="btn btn-primary btn-lg px-4 py-3 rounded-pill shadow-sm hover-lift">
                      View Full Timeline <i className="las la-arrow-right ms-2"></i>
                    </Link>
                  </div>

                  {/* Join Beta Button */}
                  <div>
                    <Link to="/register" className="btn btn-outline-light btn-lg px-5 py-3 rounded-pill hover-lift">
                      Join Beta
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <style>{`
          .hover-lift { transition: transform 0.2s; }
          .hover-lift:hover { transform: translateY(-3px); }
          @keyframes pulse {
            0% { transform: scale(1); opacity: 0.2; }
            50% { transform: scale(1.1); opacity: 0.4; }
            100% { transform: scale(1); opacity: 0.2; }
          }
          .animate-pulse { animation: pulse 3s infinite; }
          .tracking-wider { letter-spacing: 0.1em; }
        `}</style>
        </section>

        {/* Lead Magnet CTA */}
        <section className="py-5 bg-dark text-white text-center">
          <div className="container py-5">
            <h2 className="display-5 fw-bold mb-4">Ready to Secure Your Future?</h2>
            <p className="lead text-white text-opacity-75 mb-5 mx-auto max-w-[600px]">
              Join 5,000+ developers and security professionals who use Secure360 daily.
              No credit card, no commitment.
            </p>
            <div className="d-flex flex-column flex-sm-row justify-content-center gap-3">
              <Link to="/register" className="btn btn-primary btn-lg px-5 py-3">Create Free Account</Link>
              <Link to="/login" className="btn btn-outline-light btn-lg px-5 py-3">Sign In</Link>
            </div>
            <p className="mt-4 small text-white text-opacity-50">
              <i className="las la-shield-alt me-1"></i> GDPR Compliant & ISO 27001 Certified Infrastructure
            </p>
          </div>
        </section>
      </div>
    </PageLayout>
  );
};

export default Home;